import { useState, useEffect, Fragment } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

// Use unified date utils
import { moment } from '../../utils/dateUtils';
import {
  Box,
  Typography,
  Modal,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  IconButton,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
  Chip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Radio,
  RadioGroup,
  Paper,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import messages from '../../static/messages';
import proposalsServices from '../../services/proposalsServices';
import installationScheduleServices from '../../services/installationScheduleServices';
import suppliersServices from '../../services/suppliersServices';
import equipmentModelsServices from '../../services/equipmentModelsServices';
import supplyRulesServices from '../../services/supplyRulesServices';
import OrderEquipmentModal from './OrderEquipmentModal';
import {
  buildAutoOrderPlan,
  buildOrderEmailText,
  emailTextToHtml,
  isWillCallDate,
} from '../../utils/equipmentOrderUtils';

// Enable custom format parsing in dayjs
dayjs.extend(customParseFormat);

const NumberStepper = ({ value, onChange, label, min = 0, max = 99 }) => {
  const numVal = Number(value) || min;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      <Typography variant="caption" sx={{ color: '#666', mr: 0.5, fontSize: '0.7rem', minWidth: 28 }}>
        {label}
      </Typography>
      <IconButton
        size="small"
        onClick={() => onChange(Math.max(min, numVal - 1))}
        disabled={numVal <= min}
        sx={{
          border: '1px solid #ccc',
          borderRadius: '8px 0 0 8px',
          width: 36,
          height: 36,
          backgroundColor: '#f5f5f5',
          '&:active': { backgroundColor: '#e0e0e0' },
        }}
      >
        <RemoveIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Box sx={{
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderTop: '1px solid #ccc',
        borderBottom: '1px solid #ccc',
        backgroundColor: '#fff',
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{numVal}</Typography>
      </Box>
      <IconButton
        size="small"
        onClick={() => onChange(Math.min(max, numVal + 1))}
        disabled={numVal >= max}
        sx={{
          border: '1px solid #ccc',
          borderRadius: '0 8px 8px 0',
          width: 36,
          height: 36,
          backgroundColor: '#f5f5f5',
          '&:active': { backgroundColor: '#e0e0e0' },
        }}
      >
        <AddIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

const InstallationDate = ({ data, id, refresh, isSigned = false, proposal = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  const [open, setOpen] = useState(false);
  const [dates, setDates] = useState([]);
  const [confirmStep, setConfirmStep] = useState(false);
  const [pendingDates, setPendingDates] = useState([]);
  const [pendingEmergencyShortages, setPendingEmergencyShortages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [resources, setResources] = useState({});
  const [loadingResources, setLoadingResources] = useState({});
  const [emergencyBanner, setEmergencyBanner] = useState({ open: false, schedules: [] });
  const [sendingEmergencyEmails, setSendingEmergencyEmails] = useState(false);
  // Shown when the server blocks scheduling because the contract value is
  // below the configured low-price threshold (409 LOW_PRICE_REVIEW_PENDING)
  const [lowPriceBanner, setLowPriceBanner] = useState({ open: false, schedules: [] });

  // ===== Automatic equipment ordering (on scheduling) =====
  // 'auto' — send the order right after the schedule is saved (default);
  // 'manual' — open the manual order form after saving;
  // 'skip' — do not order now.
  const [orderChoice, setOrderChoice] = useState('auto');
  const [orderPlan, setOrderPlan] = useState(null); // result of buildAutoOrderPlan
  const [orderDataLoading, setOrderDataLoading] = useState(false);
  const [autoOrderCtx, setAutoOrderCtx] = useState(null); // { suppliers } cached for sending
  const [manualOrderOpen, setManualOrderOpen] = useState(false);
  const [manualOrderDate, setManualOrderDate] = useState(null);
  // Manual ordering chosen on an emergency save: the order modal opens after
  // the emergency banner is acknowledged, so the two modals don't stack.
  const [pendingManualOrder, setPendingManualOrder] = useState(false);

  // Load suppliers + catalog and compute the auto-order plan when the user
  // reaches the confirmation step.
  const loadOrderPlan = async () => {
    if (!proposal) { setOrderPlan(null); return; }
    setOrderDataLoading(true);
    try {
      const [supResp, catResp, rulesResp] = await Promise.all([
        suppliersServices.getSuppliers(),
        equipmentModelsServices.getAll({ skip: 0, take: 10000 }),
        supplyRulesServices.getAll().catch(() => ({ data: [] })),
      ]);
      const suppliers = supResp.data?.value?.data || supResp.data?.data || [];
      const models = catResp.data?.data || [];
      const catalogSuppliers = {};
      models.forEach((m) => { if (m.suppliers) catalogSuppliers[m.name] = m.suppliers; });
      const supplyRules = (rulesResp.data || []).filter((r) => r.active !== false);

      const plan = buildAutoOrderPlan({
        proposal,
        orders: proposal?.equipmentOrders || [],
        suppliers,
        catalogSuppliers,
        supplyRules,
      });
      setOrderPlan(plan);
      setAutoOrderCtx({ suppliers });
      // Nothing orderable automatically → fall back to a sensible default
      if (plan.nothingToOrder || plan.allCovered) {
        setOrderChoice('skip');
      } else if (plan.supplierGroups.length === 0) {
        // equipment exists but no supplier resolved — auto cannot help
        setOrderChoice('manual');
      } else {
        setOrderChoice('auto');
      }
    } catch (err) {
      console.error('Failed to prepare equipment order plan:', err);
      setOrderPlan(null);
      setOrderChoice('skip');
    } finally {
      setOrderDataLoading(false);
    }
  };

  // Send the automatic orders. Returns silently on any failure — scheduling
  // itself has already succeeded, ordering problems must not undo it.
  const autoPlaceOrders = async (datesSaved) => {
    if (!orderPlan || orderPlan.supplierGroups.length === 0) return;
    try {
      const earliest = [...datesSaved].map((d) => d.date).sort()[0] || null;
      const existingOrders = proposal?.equipmentOrders || [];
      const createdBy = proposal?.createdBy;
      const publicId = proposal?.publicId;

      const newOrders = [];
      for (const group of orderPlan.supplierGroups) {
        const supplier = group.supplier;
        let emailsToSend = [];
        if (supplier?.emails && Array.isArray(supplier.emails)) {
          emailsToSend.push(...supplier.emails);
        } else if (supplier?.email) {
          emailsToSend.push(supplier.email);
        }
        const plainText = buildOrderEmailText({
          supplierName: supplier?.name || 'Supplier',
          items: group.items,
          supplies: group.supplies || [],
          publicId,
          deliveryDate: earliest,
          createdBy,
          autoNote: 'This order was placed automatically when the installation was scheduled.',
        });
        newOrders.push({
          items: [...group.items, ...(group.supplies || [])].map((item) => ({
            type: item.type,
            make: item.make,
            model: item.model,
            qty: item.qty,
            instruction: item.instruction || '',
          })),
          emails: emailsToSend,
          instruction: '',
          content: emailTextToHtml(plainText),
        });
      }

      const response = await proposalsServices.update(id, {
        equipmentOrders: [...existingOrders, ...newOrders],
      });
      if (response.status !== 200) throw new Error('Failed to save equipment orders');

      const allOrders = response.data?.equipmentOrders || [];
      const existingIds = new Set(existingOrders.map((o) => o.id).filter(Boolean));
      const createdOrders = allOrders.filter((o) => !existingIds.has(o.id));

      let sentCount = 0;
      for (const savedOrder of createdOrders) {
        if ((savedOrder.emails || []).length > 0) {
          try {
            await proposalsServices.sendEquipmentOrder(savedOrder.id, savedOrder.content, createdBy?.email);
            sentCount += 1;
          } catch (emailError) {
            console.error('Failed to send auto-order email:', savedOrder.id, emailError);
          }
        }
      }

      const willCall = isWillCallDate(earliest);
      if (sentCount > 0) {
        toast.success(
          `Equipment order sent automatically to ${sentCount} supplier${sentCount !== 1 ? 's' : ''}` +
          (willCall ? ' (WILL CALL / PREPULL — job is today/tomorrow)' : ''),
        );
      } else {
        toast.warning('Equipment order was created but no supplier email was sent — please check the order.');
      }
      if (orderPlan.unresolved.length > 0) {
        toast.warning(
          `Not auto-ordered (no supplier in catalog): ${orderPlan.unresolved.map((u) => u.model).join(', ')}. Order manually.`,
          { autoClose: 12000 },
        );
      }
      refresh();
    } catch (err) {
      console.error('Auto equipment order failed:', err);
      toast.error('Automatic equipment order failed — please order manually from the proposal page.');
    }
  };

  const fetchResources = async (dateStr) => {
    if (!dateStr || loadingResources[dateStr]) return;
    setLoadingResources(prev => ({ ...prev, [dateStr]: true }));
    try {
      const resp = await installationScheduleServices.getAvailableResources(dateStr);
      const data = resp.data || resp;
      setResources(prev => ({ ...prev, [dateStr]: data }));
    } catch (err) {
      console.error('Failed to fetch resources:', err);
    } finally {
      setLoadingResources(prev => ({ ...prev, [dateStr]: false }));
    }
  };

  const getNextBusinessDay = () => {
    let next = moment().add(1, 'day');
    while (next.day() === 0 || next.day() === 6) {
      next = next.add(1, 'day');
    }
    return next.format('YYYY-MM-DD');
  };

  // Action functions
  const addDate = () =>
    setDates((prevState) => [
      ...prevState,
      {
        date: getNextBusinessDay(),
        installationTime: 'Full Day',
        installersCount: 3,
        trucksCount: 1,
        isEdited: true,
        index: prevState.length + 1,
      },
    ]);

  const removeDate = (el) => setDates((prevState) => [...prevState.filter((item) => item !== el)]);

  // Handler functions
  const handleClose = () => {
    // If on confirmation step, go back to editing instead of closing
    if (confirmStep) {
      handleConfirmCancel();
      return;
    }

    const isEdited = dates.some((item) => item.isEdited);

    if (isEdited) {
      confirm({
        title: messages.confirm_message.close_drawer,
        description: 'You have unsaved changes! All unsaved data will be lost!',
      })
        .then(() => {
          setOpen(false);
          setConfirmStep(false);
          setPendingDates([]);
          setPendingEmergencyShortages([]);
          setNote('');
          setDates(data);
        })
        .catch(() => toast.info(messages.cancel_message));
    } else {
      setOpen(false);
      setConfirmStep(false);
      setPendingDates([]);
      setPendingEmergencyShortages([]);
      setNote('');
    }
  };

  const handleChange = (event, currentItem) => {
    // Handle null event (when date is cleared)
    if (event === null) {
      // Date was cleared - keep the item but mark as edited
      setDates((prevState) => [
        ...prevState.filter((el) => el !== currentItem),
        {
          ...prevState.filter((el) => el === currentItem)[0],
          date: '',
          isEdited: true,
        },
      ]);
      return;
    }

    const key = event.target?.name || 'date';
    // For date picker, use dayjs format directly to avoid timezone issues
    // event is a dayjs object when coming from DatePicker
    let val;
    if (event.target?.value !== undefined) {
      val = event.target.value;
    } else if (event && event.format) {
      // dayjs object - format without timezone conversion
      val = event.format('YYYY-MM-DD');
    } else {
      val = moment().format('YYYY-MM-DD');
    }

    setDates((prevState) => [
      ...prevState.filter((el) => el !== currentItem),
      {
        ...prevState.filter((el) => el === currentItem)[0],
        [key]: (key === 'installersCount' || key === 'trucksCount') ? (val === 'Invalid date' ? '' : Number(val)) : val,
        isEdited: true,
      },
    ]);

    if (key === 'date' && val && val !== 'Invalid date') {
      fetchResources(val);
    }
  };

  // Prepare formatted dates for saving (deduplicates same date+time entries)
  const getFormattedDates = () => {
    const valid = dates
      .filter((item) => {
        if (!item.date || item.date === '') return false;
        const parsed = moment(item.date, ['YYYY-MM-DD', 'MM/DD/YYYY']);
        return parsed.isValid();
      })
      .map((item) => ({
        ...item,
        date: moment(item.date, ['YYYY-MM-DD', 'MM/DD/YYYY']).format('YYYY-MM-DD'),
      }));

    const seen = new Map();
    return valid.filter((item) => {
      const key = `${item.date}_${item.installationTime}`;
      if (seen.has(key)) return false;
      seen.set(key, true);
      return true;
    });
  };

  const getAvailabilityBySlot = (dateItem) => {
    const r = resources[dateItem.date];
    if (!r) return null;

    const slot = dateItem.installationTime;
    const reqTrucks = Number(dateItem.trucksCount ?? 1);
    const reqInstallers = Number(dateItem.installersCount) || 1;

    const fullT = r.trucks?.available ?? 0;
    const fullI = r.installers?.available ?? 0;
    const amOnlyT = Math.max(0, (r.trucks?.availableAm ?? 0) - fullT);
    const pmOnlyT = Math.max(0, (r.trucks?.availablePm ?? 0) - fullT);
    const amOnlyI = Math.max(0, (r.installers?.availableAm ?? 0) - fullI);
    const pmOnlyI = Math.max(0, (r.installers?.availablePm ?? 0) - fullI);

    const totalSlotT = slot === 'AM' ? fullT + amOnlyT : slot === 'PM' ? fullT + pmOnlyT : fullT;
    const totalSlotI = slot === 'AM' ? fullI + amOnlyI : slot === 'PM' ? fullI + pmOnlyI : fullI;

    return {
      reqTrucks,
      reqInstallers,
      totalSlotT,
      totalSlotI,
      overT: reqTrucks > totalSlotT,
      overI: reqInstallers > totalSlotI,
    };
  };

  const buildEmergencyShortages = (formattedDates) => {
    // Ignore dates in the past: the work was already done for those days,
    // their availability snapshot reflects past allocation and would falsely
    // trigger the emergency-request flow when adding a new future date.
    const today = moment().format('YYYY-MM-DD');
    return formattedDates
      .map((d) => {
        if (d.date < today) return null;
        const availability = getAvailabilityBySlot(d);
        if (!availability) return null;
        if (!availability.overT && !availability.overI) return null;

        return {
          date: d.date,
          installationTime: d.installationTime,
          requestedInstallers: availability.reqInstallers,
          requestedTrucks: availability.reqTrucks,
          availableInstallers: availability.totalSlotI,
          availableTrucks: availability.totalSlotT,
        };
      })
      .filter(Boolean);
  };

  // Step 1: User clicks Save -> check for today's dates, then show confirmation screen
  const handleSaveClick = () => {
    const formattedDates = getFormattedDates();
    const shortages = buildEmergencyShortages(formattedDates);
    setPendingEmergencyShortages(shortages);

    if (formattedDates.length === 0) {
      doSave(formattedDates);
      return;
    }

    const today = moment().format('YYYY-MM-DD');
    const hasTodayDate = formattedDates.some((d) => d.date === today);

    if (hasTodayDate) {
      confirm({
        title: 'WARNING: Scheduling for TODAY',
        description:
          'One or more installation dates are set to TODAY. Are you sure you want to schedule the installation for today?',
        confirmationText: 'Yes, schedule for today',
        cancellationText: 'Go back and change',
      })
        .then(() => {
          setPendingDates(formattedDates);
          setConfirmStep(true);
          loadOrderPlan();
        })
        .catch(() => {});
    } else {
      setPendingDates(formattedDates);
      setConfirmStep(true);
      loadOrderPlan();
    }
  };

  // Step 2: Actually save to server
  const doSave = async (datesToSave) => {
    if (saving) return;
    setSaving(true);
    try {
      const hasEmergencyShortage = pendingEmergencyShortages.length > 0;

      if (hasEmergencyShortage) {
        const response = await proposalsServices.saveEmergencyScheduleRequest(id, {
          schedules: datesToSave,
          shortages: pendingEmergencyShortages,
          note: note.trim() || undefined,
        });

        if (response.status === 200) {
          refresh();
          setConfirmStep(false);
          setPendingDates([]);
          setOpen(false);
          setEmergencyBanner({ open: true, schedules: datesToSave });
          toast.warning('Request saved. Management review is required.');

          // Equipment is ordered even on an emergency request — easier to
          // return it later than to have the crew show up without it.
          if (proposal && datesToSave.length > 0) {
            if (orderChoice === 'auto' && orderPlan?.supplierGroups?.length > 0) {
              await autoPlaceOrders(datesToSave);
            } else if (orderChoice === 'manual') {
              const earliest = [...datesToSave].map((d) => d.date).sort()[0] || null;
              setManualOrderDate(earliest);
              setPendingManualOrder(true); // opens after the banner is acknowledged
            }
          }
        }
      } else {
        const payload = { installationSchedule: datesToSave };
        if (note.trim()) {
          payload.installationScheduleNote = note.trim();
        }
        const response = await proposalsServices.update(id, payload);

        if (response.status === 200) {
          toast.success('Installation Schedule successfully updated');
          refresh();
          setConfirmStep(false);
          setPendingDates([]);
          setNote('');
          setOpen(false);

          // ===== Equipment ordering (only on a normal, accepted save) =====
          if (proposal && datesToSave.length > 0) {
            if (orderChoice === 'auto' && orderPlan?.supplierGroups?.length > 0) {
              await autoPlaceOrders(datesToSave);
            } else if (orderChoice === 'manual') {
              const earliest = [...datesToSave].map((d) => d.date).sort()[0] || null;
              setManualOrderDate(earliest);
              setManualOrderOpen(true);
            }
          }
        }
      }
    } catch (error) {
      // Low-price guard: the schedule was NOT saved, a review request was
      // created and management was notified by the server.
      if (error?.response?.status === 409 && error?.response?.data?.message === 'LOW_PRICE_REVIEW_PENDING') {
        setConfirmStep(false);
        setPendingDates([]);
        setPendingEmergencyShortages([]);
        setNote('');
        setOpen(false);
        setLowPriceBanner({ open: true, schedules: datesToSave });
        toast.warning('Request sent for management review.');
      } else {
        toast.error(messages.errors.error_try_again);
        console.log(error);
      }
    } finally {
      setSaving(false);
    }
  };

  // Cancel confirmation -> go back to editing
  const handleConfirmCancel = () => {
    setConfirmStep(false);
    setPendingDates([]);
    setPendingEmergencyShortages([]);
  };

  const handleEmergencyBannerAcknowledge = async () => {
    if (sendingEmergencyEmails) return;
    setSendingEmergencyEmails(true);
    try {
      const response = await proposalsServices.notifyEmergencyScheduleRequest(id);
      if (response.status === 200) {
        toast.success('Emergency request sent to management contacts and requester confirmation sent.');
        setEmergencyBanner({ open: false, schedules: [] });
        setNote('');
        setPendingEmergencyShortages([]);
        if (pendingManualOrder) {
          setPendingManualOrder(false);
          setManualOrderOpen(true);
        }
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    } finally {
      setSendingEmergencyEmails(false);
    }
  };

  // Hooks
  useEffect(() => {
    if (open && dates.length > 0) {
      const seen = new Set();
      dates.forEach(d => {
        if (d.date && !seen.has(d.date)) {
          seen.add(d.date);
          fetchResources(d.date);
        }
      });
    }
  }, [open, dates.length]);

  useEffect(() => {
    if (data.length) {
      setDates(
        data.map((el, idx) => {
          // Parse date from server (YYYY-MM-DD or ISO format) and keep as local date string
          let dateStr = el.date;
          if (dateStr) {
            // If it's an ISO string with time/timezone, extract just the date part
            if (typeof dateStr === 'string' && dateStr.includes('T')) {
              dateStr = dateStr.split('T')[0];
            }
            // Ensure YYYY-MM-DD format
            const parsed = moment(dateStr, ['YYYY-MM-DD', 'MM/DD/YYYY']);
            if (parsed.isValid()) {
              dateStr = parsed.format('YYYY-MM-DD');
            }
          }
          return {
            ...el,
            date: dateStr,
            index: idx + 1,
          };
        }),
      );
    }
  }, [data]);

  if (!isSigned) {
    // No dates yet → prompt to sign first. Already-scheduled dates stay visible
    // (read-only) so signing isn't required just to see them.
    if (dates.length === 0) {
      return (
        <Alert severity="info" sx={{ my: 1 }}>
          To select an installation date, the proposal must be signed by customer.
        </Alert>
      );
    }
    return (
      <Box sx={{ pt: 1, pb: 1 }}>
        {[...dates]
          .sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0))
          .map((el, index) => {
            const date = moment(el.date, ['YYYY-MM-DD', 'MM/DD/YYYY']);
            const formattedDate = date.isValid() ? date.format('MM/DD/YYYY') : 'No date';
            return (
              <Typography key={index}>
                {formattedDate} - {el.installationTime} - {el.installersCount} Installers - {el.trucksCount ?? 1} Truck{Number(el.trucksCount ?? 1) !== 1 ? 's' : ''}
              </Typography>
            );
          })}
      </Box>
    );
  }

  return (
    <Fragment>
      <Box
        sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setOpen(true)}
      >
        <Box sx={{ pt: 1, pb: 1 }}>
          {dates.length > 0 ? (
            dates
              ?.sort(function (a, b) {
                if (a.index < b.index) {
                  return -1;
                }
                if (a.index > b.index) {
                  return 1;
                }
                return 0;
              })
              .map((el, index) => {
                // Parse date - could be YYYY-MM-DD or MM/DD/YYYY format
                const date = moment(el.date, ['YYYY-MM-DD', 'MM/DD/YYYY']);
                const formattedDate = date.isValid() ? date.format('MM/DD/YYYY') : 'No date';
                return (
                  <Typography key={index}>
                    {formattedDate} - {el.installationTime} - {el.installersCount} Installers - {el.trucksCount ?? 1} Truck{Number(el.trucksCount ?? 1) !== 1 ? 's' : ''}
                  </Typography>
                );
              })
          ) : (
            <Typography onClick={() => setOpen(true)}>No dates set, click here to add</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CalendarMonthIcon />
        </Box>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box sx={{ 
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isMobile ? '95%' : 800,
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          backgroundColor: '#fff',
          boxShadow: 24,
          p: isMobile ? 2 : 4,
          borderRadius: 1,
        }}>
          {confirmStep ? (
            /* ===== Confirmation step ===== */
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                <WarningAmberIcon color="warning" fontSize="large" />
                <Typography variant={isMobile ? 'h6' : 'h5'}>
                  <strong>Confirm installation schedule</strong>
                </Typography>
              </Box>

              <Typography variant="body1" align="center" sx={{ mb: 2, color: 'text.secondary' }}>
                You are saving <strong>{pendingDates.length} installation date{pendingDates.length !== 1 ? 's' : ''}</strong>. Please review:
              </Typography>

              {pendingEmergencyShortages.length > 0 && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 2,
                    bgcolor: '#ff1744',
                    color: '#fff',
                    border: '1px solid #d50000',
                    '& .MuiAlert-icon': { color: '#fff' },
                    '& .MuiAlert-message': { color: '#fff', fontWeight: 600 },
                  }}
                >
                  Requested resources exceed current availability for {pendingEmergencyShortages.length} date
                  {pendingEmergencyShortages.length !== 1 ? 's' : ''}. This will be stored as an emergency request.
                </Alert>
              )}

              <Divider sx={{ mb: 2 }} />

              {pendingDates
                .sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0))
                .map((el, index) => {
                  const date = moment(el.date, 'YYYY-MM-DD');
                  const formattedDate = date.isValid() ? date.format('MM/DD/YYYY (dddd)') : 'Invalid date';
                  const isToday = el.date === moment().format('YYYY-MM-DD');
                  return (
                    <Box key={index} sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      py: 1.5, 
                      px: 2,
                      mb: 1,
                      backgroundColor: isToday ? '#fff8e1' : '#f5f5f5',
                      borderRadius: 1,
                      border: isToday ? '2px solid #f9a825' : '1px solid #e0e0e0',
                    }}>
                      <Typography variant="body1">
                        <strong>#{index + 1}</strong>&nbsp;&nbsp;{formattedDate}
                        {isToday && <strong style={{ color: '#e65100', marginLeft: 8 }}>TODAY</strong>}
                      </Typography>
                      <Typography variant="body1">
                        {el.installationTime} - {el.installersCount} Installer{Number(el.installersCount) !== 1 ? 's' : ''} - {el.trucksCount ?? 1} Truck{Number(el.trucksCount ?? 1) !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  );
                })}

              <Divider sx={{ mt: 2, mb: 2 }} />

              <TextField
                label="Note for office (optional)"
                placeholder="Add a message that will be included in the email notification..."
                multiline
                minRows={2}
                maxRows={4}
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
                sx={{ mb: 2 }}
                size="small"
              />

              {/* ===== Equipment ordering (also on emergency requests —
                   better to return equipment later than to miss the job) ===== */}
              {proposal && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8f9fa' }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocalShippingIcon fontSize="small" color="primary" />
                    <strong>Equipment ordering</strong>
                  </Typography>
                  {pendingEmergencyShortages.length > 0 && (
                    <Alert severity="info" sx={{ mb: 1, py: 0 }}>
                      Equipment will be ordered even though this is an emergency request.
                    </Alert>
                  )}

                  {orderDataLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="body2" color="text.secondary">Checking equipment and suppliers...</Typography>
                    </Box>
                  ) : !orderPlan ? (
                    <Typography variant="body2" color="text.secondary">
                      Equipment info is not available — order manually from the proposal page if needed.
                    </Typography>
                  ) : orderPlan.nothingToOrder ? (
                    <Typography variant="body2" color="text.secondary">
                      No equipment on this contract — nothing to order.
                    </Typography>
                  ) : orderPlan.allCovered ? (
                    <Alert severity="success" sx={{ py: 0 }}>
                      All equipment has already been ordered.
                    </Alert>
                  ) : (
                    <>
                      {pendingDates.length > 0 && isWillCallDate([...pendingDates].map((d) => d.date).sort()[0]) && (
                        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                          Job is today/tomorrow — the order will be marked <strong>WILL CALL / PREPULL</strong> for
                          pickup by our courier.
                        </Alert>
                      )}
                      <RadioGroup value={orderChoice} onChange={(e) => setOrderChoice(e.target.value)}>
                        <FormControlLabel
                          value="auto"
                          control={<Radio size="small" />}
                          disabled={orderPlan.supplierGroups.length === 0}
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Order automatically now (recommended)
                              </Typography>
                              {orderPlan.supplierGroups.map((g) => (
                                <Typography key={g.supplier.id} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  → {g.supplier.name}: {[
                                    ...g.items.map((i) => i.model),
                                    ...(g.supplies || []).map((s) => (s.qty > 1 ? `${s.model} ×${s.qty}` : s.model)),
                                  ].join(', ')}
                                </Typography>
                              ))}
                              {orderPlan.alreadyOrdered.length > 0 && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                  Already ordered: {orderPlan.alreadyOrdered.map((i) => i.model).join(', ')}
                                </Typography>
                              )}
                              {orderPlan.unresolved.length > 0 && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                  No supplier in catalog (order manually): {orderPlan.unresolved.map((i) => i.model).join(', ')}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', mb: 1 }}
                        />
                        <FormControlLabel
                          value="manual"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">I will order manually (opens the order form after saving)</Typography>}
                        />
                        <FormControlLabel
                          value="skip"
                          control={<Radio size="small" />}
                          label={<Typography variant="body2">Do not order now</Typography>}
                        />
                      </RadioGroup>
                    </>
                  )}
                </Paper>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  onClick={handleConfirmCancel} 
                  size={isMobile ? 'small' : 'medium'}
                >
                  Go Back
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={() => doSave(pendingDates)} 
                  disabled={saving}
                  size={isMobile ? 'small' : 'medium'}
                >
                  {saving ? 'Saving...' : 'Confirm & Save'}
                </Button>
              </Box>
            </>
          ) : (
            /* ===== Edit step ===== */
            <>
              <Typography variant={isMobile ? 'h6' : 'h5'} align="center" sx={{ mb: 2 }}>
                <strong>Select dates below to schedule installers</strong>
              </Typography>

              {dates.length > 0 ? (
                dates
                  .sort(function (a, b) {
                    if (a.index < b.index) {
                      return -1;
                    }
                    if (a.index > b.index) {
                      return 1;
                    }
                    return 0;
                  })
                  .map((el, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        mb: 2,
                        pb: 1.5,
                        borderBottom: '1px solid #e0e0e0',
                      }}
                    >
                      {/* Row 1: Date + Time Slot + Installers + Trucks + Delete */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 1,
                        alignItems: isMobile ? 'stretch' : 'center',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const cur = el.date ? dayjs(el.date, 'YYYY-MM-DD') : dayjs();
                              handleChange(cur.subtract(1, 'day'), el);
                            }}
                            sx={{ border: '1px solid #ccc', borderRadius: '8px 0 0 8px', width: 32, height: 40, backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' } }}
                          >
                            <ChevronLeftIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                              label="Date"
                              inputFormat="MM/DD/YYYY"
                              value={el.date ? dayjs(el.date, 'YYYY-MM-DD') : null}
                              onChange={(event) => handleChange(event, el)}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  size="small" 
                                  name="date" 
                                  onChange={(event) => handleChange(event, el)}
                                  fullWidth={isMobile}
                                  sx={{ minWidth: 140, maxWidth: isMobile ? undefined : 160, '& .MuiInputBase-root': { borderRadius: 0 } }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const cur = el.date ? dayjs(el.date, 'YYYY-MM-DD') : dayjs();
                              handleChange(cur.add(1, 'day'), el);
                            }}
                            sx={{ border: '1px solid #ccc', borderRadius: '0 8px 8px 0', width: 32, height: 40, backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' } }}
                          >
                            <ChevronRightIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </Box>

                        <ToggleButtonGroup
                          color="primary"
                          value={el.installationTime}
                          exclusive
                          onChange={(event) => handleChange(event, el)}
                          size="small"
                        >
                          <ToggleButton value="AM" name="installationTime">AM</ToggleButton>
                          <ToggleButton value="PM" name="installationTime">PM</ToggleButton>
                          <ToggleButton value="Full Day" name="installationTime" sx={{ fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                            {isMobile ? 'Full' : 'Full Day'}
                          </ToggleButton>
                        </ToggleButtonGroup>

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {isMobile ? (
                            <>
                              <NumberStepper
                                label="Crew"
                                value={el.installersCount}
                                min={1}
                                onChange={(val) => handleChange({ target: { name: 'installersCount', value: val } }, el)}
                              />
                              <NumberStepper
                                label="Trucks"
                                value={el.trucksCount ?? 1}
                                min={0}
                                onChange={(val) => handleChange({ target: { name: 'trucksCount', value: val } }, el)}
                              />
                            </>
                          ) : (
                            <>
                              <TextField
                                required
                                name="installersCount"
                                label="Installers"
                                size="small"
                                type="number"
                                value={el.installersCount}
                                onChange={(event) => handleChange(event, el)}
                                sx={{ width: 90 }}
                              />
                              <TextField
                                required
                                name="trucksCount"
                                label="Trucks"
                                size="small"
                                type="number"
                                value={el.trucksCount ?? 1}
                                onChange={(event) => handleChange(event, el)}
                                inputProps={{ min: 0 }}
                                sx={{ width: 75 }}
                              />
                            </>
                          )}
                          <Tooltip title="Remove">
                            <IconButton onClick={() => removeDate(el)} color="error" size="small">
                              <DeleteForeverIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* Row 2: Available Resources Panel */}
                      {el.date && (
                        <Box sx={{ pl: 0.5 }}>
                          {loadingResources[el.date] ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={12} />
                              <Typography variant="caption" color="text.secondary">Loading resources...</Typography>
                            </Box>
                          ) : resources[el.date] ? (
                            (() => {
                              const r = resources[el.date];
                              const slot = el.installationTime;
                              const reqTrucks = Number(el.trucksCount ?? 1);
                              const reqInst = Number(el.installersCount) || 1;

                              const fullT = r.trucks?.available ?? 0;
                              const fullI = r.installers?.available ?? 0;
                              const amOnlyT = Math.max(0, (r.trucks?.availableAm ?? 0) - fullT);
                              const pmOnlyT = Math.max(0, (r.trucks?.availablePm ?? 0) - fullT);
                              const amOnlyI = Math.max(0, (r.installers?.availableAm ?? 0) - fullI);
                              const pmOnlyI = Math.max(0, (r.installers?.availablePm ?? 0) - fullI);

                              const totalSlotT = slot === 'AM' ? fullT + amOnlyT : slot === 'PM' ? fullT + pmOnlyT : fullT;
                              const totalSlotI = slot === 'AM' ? fullI + amOnlyI : slot === 'PM' ? fullI + pmOnlyI : fullI;
                              const overT = reqTrucks > totalSlotT;
                              const overI = reqInst > totalSlotI;
                              const isOver = overT || overI;

                              const cols = [
                                { key: 'full', label: 'Full Day', tv: fullT, iv: fullI },
                                { key: 'am', label: 'AM only', tv: amOnlyT, iv: amOnlyI },
                                { key: 'pm', label: 'PM only', tv: pmOnlyT, iv: pmOnlyI },
                              ];
                              const isActive = (k) => k === 'full' || (k === 'am' && slot === 'AM') || (k === 'pm' && slot === 'PM');
                              const slotLabel = slot === 'AM' ? 'AM' : slot === 'PM' ? 'PM' : 'Full Day';

                              return (
                                <Box sx={{
                                  p: 1.5, mt: 0.5,
                                  backgroundColor: isOver ? '#fff8e1' : '#f8f9fa',
                                  borderRadius: 1,
                                  border: `1px solid ${isOver ? '#ffcc80' : '#e0e0e0'}`,
                                }}>
                                  <Box sx={{ display: 'grid', gridTemplateColumns: '85px repeat(3, 1fr)', gap: '4px 8px', alignItems: 'center' }}>
                                    <Box />
                                    {cols.map(c => (
                                      <Typography key={c.key} variant="caption" sx={{
                                        textAlign: 'center', fontWeight: 700, fontSize: '0.7rem',
                                        color: isActive(c.key) ? '#1976d2' : '#bbb',
                                        borderBottom: isActive(c.key) ? '2px solid #1976d2' : '2px solid transparent',
                                        pb: 0.3,
                                      }}>
                                        {c.label}
                                      </Typography>
                                    ))}

                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                      {'\u{1F69B}'} Trucks
                                    </Typography>
                                    {cols.map(c => {
                                      const v = c.tv;
                                      const active = isActive(c.key);
                                      const isDash = c.key !== 'full' && v === 0;
                                      return (
                                        <Chip key={c.key} size="small"
                                          variant={active && !isDash ? 'filled' : 'outlined'}
                                          label={isDash ? '\u2014' : v}
                                          color={active && !isDash ? 'primary' : 'default'}
                                          sx={{
                                            justifySelf: 'center', fontSize: isDash ? '0.9rem' : '0.8rem',
                                            fontWeight: active && !isDash ? 700 : 400, height: 24, minWidth: 40,
                                            ...(isDash && { color: '#ccc', borderColor: '#e8e8e8' }),
                                          }}
                                        />
                                      );
                                    })}

                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                      {'\u{1F477}'} Workers
                                    </Typography>
                                    {cols.map(c => {
                                      const v = c.iv;
                                      const active = isActive(c.key);
                                      const isDash = c.key !== 'full' && v === 0;
                                      return (
                                        <Chip key={c.key} size="small"
                                          variant={active && !isDash ? 'filled' : 'outlined'}
                                          label={isDash ? '\u2014' : v}
                                          color={active && !isDash ? 'primary' : 'default'}
                                          sx={{
                                            justifySelf: 'center', fontSize: isDash ? '0.9rem' : '0.8rem',
                                            fontWeight: active && !isDash ? 700 : 400, height: 24, minWidth: 40,
                                            ...(isDash && { color: '#ccc', borderColor: '#e8e8e8' }),
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>

                                  <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid #eaeaea' }}>
                                    <Typography variant="body2" sx={{
                                      fontWeight: 700, fontSize: '0.8rem',
                                      color: isOver ? '#d32f2f' : '#1976d2',
                                    }}>
                                      {'\u25B8'} {slotLabel}: {totalSlotT} truck{totalSlotT !== 1 ? 's' : ''} / {totalSlotI} worker{totalSlotI !== 1 ? 's' : ''} available
                                      {slot !== 'Full Day' && ` (${fullT} full day + ${slot === 'AM' ? amOnlyT : pmOnlyT} ${slot} only)`}
                                    </Typography>
                                  </Box>

                                  <Box sx={{ mt: 0.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem' }}>
                                      Physical: <strong>{r.trucks?.total ?? '?'}</strong>T / <strong>{r.installers?.total ?? '?'}</strong>W
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#666', fontSize: '0.65rem' }}>
                                      Scheduled: <strong>{r.trucks?.scheduled ?? 0}</strong>T / <strong>{r.installers?.scheduled ?? 0}</strong>W
                                    </Typography>
                                    {(r.trucks?.unavailable > 0 || r.installers?.unavailable > 0) && (
                                      <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>
                                        Off: {r.trucks?.unavailable ?? 0}T / {r.installers?.unavailable ?? 0}W
                                      </Typography>
                                    )}
                                  </Box>

                                  {isOver && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip size="small" icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                                        label={`Exceeds! Need ${overT ? reqTrucks + 'T' : ''}${overT && overI ? ' & ' : ''}${overI ? reqInst + 'W' : ''}, have ${overT ? totalSlotT + 'T' : ''}${overT && overI ? ' & ' : ''}${overI ? totalSlotI + 'W' : ''}`}
                                        color="warning"
                                        sx={{ fontSize: '0.7rem', height: 24, fontWeight: 600 }}
                                      />
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()
                          ) : null}
                        </Box>
                      )}
                    </Box>
                  ))
              ) : (
                <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                  <strong>Add a first date</strong>
                </Typography>
              )}

              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                justifyContent: 'space-between',
                flexDirection: 'row',
                mt: 1,
              }}>
                <Button variant="contained" color="error" onClick={addDate} size={isMobile ? 'small' : 'medium'}>
                  Add New Date
                </Button>
                <Button variant="contained" onClick={handleSaveClick} size={isMobile ? 'small' : 'medium'}>
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <Modal open={emergencyBanner.open} onClose={() => {}}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : 760,
            maxWidth: 760,
            backgroundColor: '#b71c1c',
            color: '#fff',
            boxShadow: 24,
            p: isMobile ? 2 : 4,
            borderRadius: 1,
            border: '3px solid #ff8a80',
          }}
        >
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
            Work is NOT added to live schedule
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: 1.6, mb: 2 }}>
            Requested date and resources were saved in the proposal as an emergency request.
            Management will review this request before scheduling.
          </Typography>
          {emergencyBanner.schedules?.length > 0 && (
            <Box sx={{ mb: 3, background: 'rgba(255,255,255,0.12)', p: 2, borderRadius: 1 }}>
              {emergencyBanner.schedules.map((s, i) => (
                <Typography key={`${s.date}-${s.installationTime}-${i}`} sx={{ fontSize: 14, mb: 0.5 }}>
                  #{i + 1} {moment(s.date, 'YYYY-MM-DD').format('MM/DD/YYYY')} - {s.installationTime} - {s.installersCount} Installers - {s.trucksCount ?? 1} Trucks
                </Typography>
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="inherit"
              onClick={handleEmergencyBannerAcknowledge}
              disabled={sendingEmergencyEmails}
              sx={{ color: '#b71c1c', fontWeight: 700, minWidth: 180 }}
            >
              {sendingEmergencyEmails ? 'Sending...' : "I'm Agree"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={lowPriceBanner.open} onClose={() => {}}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '95%' : 760,
            maxWidth: 760,
            backgroundColor: '#e65100',
            color: '#fff',
            boxShadow: 24,
            p: isMobile ? 2 : 4,
            borderRadius: 1,
            border: '3px solid #ffcc80',
          }}
        >
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
            Work is NOT added to the schedule
          </Typography>
          <Typography sx={{ fontSize: 16, lineHeight: 1.6, mb: 2 }}>
            The contract value is below the minimum set for this period, so the request was sent for
            management review. You will be notified by email once it is approved or declined.
          </Typography>
          {lowPriceBanner.schedules?.length > 0 && (
            <Box sx={{ mb: 3, background: 'rgba(255,255,255,0.12)', p: 2, borderRadius: 1 }}>
              {lowPriceBanner.schedules.map((s, i) => (
                <Typography key={`${s.date}-${s.installationTime}-${i}`} sx={{ fontSize: 14, mb: 0.5 }}>
                  #{i + 1} {moment(s.date, 'YYYY-MM-DD').format('MM/DD/YYYY')} - {s.installationTime} - {s.installersCount} Installers - {s.trucksCount ?? 1} Trucks
                </Typography>
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => setLowPriceBanner({ open: false, schedules: [] })}
              sx={{ color: '#e65100', fontWeight: 700, minWidth: 180 }}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Manual ordering chosen at scheduling time — the regular order form */}
      {proposal && manualOrderOpen && (
        <OrderEquipmentModal
          open={manualOrderOpen}
          onClose={() => { setManualOrderOpen(false); refresh(); }}
          systems={proposal?.proposalSystems || []}
          proposal={proposal}
          orders={proposal?.equipmentOrders || []}
          publicId={proposal?.publicId}
          proposalId={id}
          refresh={refresh}
          additionalNotes={proposal?.additionalNotes}
          onSaveNotes={async (notes) => {
            try {
              await proposalsServices.update(id, { additionalNotes: notes });
            } catch (err) {
              console.error('Failed to save notes:', err);
            }
          }}
          installationSchedule={manualOrderDate ? [{ date: manualOrderDate }] : []}
          createdBy={proposal?.createdBy}
        />
      )}
    </Fragment>
  );
};

export default InstallationDate;
